// ModernPricingPage.js
import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { RippleButton } from "./RippleButton";
import { useNavigate } from "react-router-dom";
import Simple from "../../../Components/InputFields/Simple";
import { toPersianDigits } from "../../../helper/toPersianDigits";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";
import Spinner from "../../../Components/Loader/Spinner";

const EXPECTED_AMOUNT_URL = `${API_BASE_URL}payment-payout/initialized/expected-amount/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;
const DEFAULT_COUPON_MESSAGE = "هنوز کد تخفیفی اعمال نشده است.";

// --- Internal Helper Components (Not exported) --- //

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// --- EXPORTED Building Blocks --- //

export const PricingCard = ({
  planName,
  description,
  price,
  discountPrice,
  discountPercent,
  features,
  buttonText,
  isPopular = false,
  buttonVariant = "primary",
}) => {
  const navigate = useNavigate();
  const hasDiscount = Boolean(discountPrice);
  const discountLabel = discountPercent
    ? `${toPersianDigits(discountPercent)}٪`
    : "";
  const [payablePriceIrr, setPayablePriceIrr] = useState("");
  const [payablePrice, setPayablePrice] = useState("");
  const [isExpectedAmountLoading, setIsExpectedAmountLoading] = useState(true);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const couponInFlightRef = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchWithAuthRetry = useCallback(
    async (url, options = {}) => {
      const getRequestOptions = () => ({
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      let response = await fetch(url, getRequestOptions());

      if (response.status !== 401) {
        return response;
      }

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) {
        return null;
      }

      return fetch(url, getRequestOptions());
    },
    [refreshAccessToken],
  );

  const fetchExpectedAmount = useCallback(async () => {
    setIsExpectedAmountLoading(true);
    try {
      const response = await fetchWithAuthRetry(EXPECTED_AMOUNT_URL, {
        method: "GET",
      });
      if (!response) {
        return;
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      const expectedAmount =
        data?.expected_amount_usd ?? data?.data?.expected_amount_usd;
      const expectedAmountIrr =
        data?.expected_amount_irr ?? data?.data?.expected_amount_irr;

      if (expectedAmount !== undefined && expectedAmount !== null) {
        setPayablePrice(Number(expectedAmount).toFixed(2));
        setPayablePriceIrr(Number(expectedAmountIrr));
      }
    } catch (error) {
      console.error("Error fetching expected amount:", error);
    } finally {
      setIsExpectedAmountLoading(false);
    }
  }, [fetchWithAuthRetry]);

  useEffect(() => {
    fetchExpectedAmount();
  }, [fetchExpectedAmount]);

  const cardClasses = `
    w-full max-w-[30rem] backdrop-blur-[14px] mx-auto rounded-2xl flex-1 min-w-fit p-3 sm:p-5 flex flex-col md:flex-row transition-all duration-300
    bg-zi-bg2-light dark:bg-zi-bg2-dark border border-transparent
    ${isPopular ? "relative " : ""}
  `;
  const [ruleAgree, setRuleAgree] = useState(false);
  const canContinue =
    ruleAgree &&
    Boolean(payablePrice) &&
    !isExpectedAmountLoading &&
    !isCouponLoading;
  const buttonClasses = `
  ${canContinue ? "bg-zi-gradient-button-blue" : "bg-gray-500 !cursor-not-allowed"}
     w-full py-1 rounded-xl font-medium text-base transition mt-6
    ${
      isPopular
      // ? "bg-cyan-400 hover:bg-cyan-300 !text-black"
      // : "border bg-white/10 hover:bg-white/20 text-white border-white/20"
    }
  `;
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState(DEFAULT_COUPON_MESSAGE);
  const handlePay = async () => {
    if (!canContinue) {
      return;
    }
    navigate("/purchase/confirm/payment", {
      state: {
        plan: {
          planName,
          description,
          price,
          discountPrice,
          discountPercent,
          features,
          buttonText,
          isPopular,
          buttonVariant,
        },
        planName,
        payablePrice,
        payablePriceIrr,
      },
    });
  };

  const handleCoupon = async () => {
    if (!couponCode || couponInFlightRef.current) return;
    couponInFlightRef.current = true;
    setIsCouponLoading(true);
    try {
      let response = await fetch(
        `${API_BASE_URL}payment-payout/coupons/apply/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({ name: couponCode }),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(`${API_BASE_URL}payment-payout/coupons/apply/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({ name: couponCode }),
        });
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        setCouponMessage("کد تخفیف نامعتبر است.");
        return;
      }

      setCouponMessage(`کد تخفیف با موفقیت اعمال شد!`);
      await fetchExpectedAmount();
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      couponInFlightRef.current = false;
      setIsCouponLoading(false);
    }
  };

  return (
    <div className={cardClasses.trim()}>
      {hasDiscount && (
        <div className="hidden absolute -top-4 right-4 px-3 py-1 text-sm font-normal rounded-full bg-zi-blue-light text-zi-text-dark">
          {discountLabel ? `${discountLabel} تخفیف` : "تخفیف"}
        </div>
      )}
      <div className="flex-1">
        <div className="pb-6">
          <p className="text-2xl font-thin text-center w-full text-zi-text-light/50 dark:text-zi-text-dark/50 pb-2.5">
            چالش انتخاب شده
          </p>
          <h2 className="text-[48px] text-center font-extralight tracking-[-0.03em] text-zi-text-light dark:text-zi-text-dark ">
            {planName}
          </h2>
          <div className="w-full flex justify-center items-center">
            <div className="flex flex-col gap-0">
              <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark mt-0 font-sans">
                {description}
              </p>
              <span className="text-[14px] text-zi-text-light/70 dark:text-zi-text-dark/70">
                سرمایه اولیه
              </span>
            </div>
          </div>
        </div>

        <div className="hidden card-divider w-full h-px my-3 bg-zi-border-dark/50 dark:bg-zi-border-light/50" />

        <div className="w-full flex flex-col justify-center items-center">
          <div className="flex items-center justify-between gap-2 w-full">
            <span className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
              هزینه چالش
            </span>
            <span className="text-lg font-extralight text-zi-text-light dark:text-zi-text-dark">
              ${price}
            </span>
          </div>

          {hasDiscount && (
            <>
              <div className="card-divider w-full h-px my-3 bg-zi-border-dark/50 dark:bg-zi-border-light/50" />

              <div className="flex items-center justify-between gap-2 w-full">
                <span className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                  هزینه پس از تخفیف
                  {discountLabel ? ` ( ${discountLabel} )` : ""}
                </span>
                <span className="text-lg font-extralight text-zi-text-light dark:text-zi-text-dark">
                  ${discountPrice}
                </span>
              </div>
            </>
          )}
          <div className=" card-divider w-full h-px my-3 bg-zi-border-dark/50 dark:bg-zi-border-light/50" />
          {!hasDiscount && (
            <>
              <div className="flex flex-col gap-1 w-full">
                <div className="flex items-center justify-between gap-2 w-full pt-0">
                  <label
                    htmlFor="coupon"
                    className="hidden text-sm text-zi-text-light/70 dark:text-zi-text-dark/70"
                  >
                    کد تخفیف دارید؟
                  </label>
                  <input
                    placeholder="کد تخفیف دارید؟"
                    value={couponCode}
                    onChange={(e) => {
                      const nextCouponCode = e.target.value;
                      setCouponCode(nextCouponCode);
                      setCouponMessage(
                        nextCouponCode ? "" : DEFAULT_COUPON_MESSAGE,
                      );
                    }}
                    type="text"
                    id="coupon"
                    className="bg-transparent px-2 rounded-lg border border-zi-border-dark/50 hocus:border-zi-border-dark w-full text-sm text-zi-text-light dark:text-zi-text-dark py-2"
                  />
                  <button
                    onClick={handleCoupon}
                    type="button"
                    disabled={isCouponLoading || !couponCode}
                    className={`min-w-14 px-3 py-2 rounded-lg text-zi-text-dark text-sm flex items-center justify-center hocus:ring-1 ring-inset ring-offset-1 ring-offset-zi-blue-light hocus:ring-white ${
                      isCouponLoading || !couponCode
                        ? "bg-gray-500 cursor-not-allowed"
                        : "bg-zi-gradient-button-blue cursor-pointer"
                    }`}
                  >
                    {isCouponLoading ? (
                      <Spinner size={18} color="white" stroke={3} />
                    ) : (
                      "اعمال"
                    )}
                  </button>
                </div>
                <span className="text-xs text-zi-text-light/50 dark:text-zi-text-dark/50">
                  {couponMessage}
                </span>
              </div>
              <div className="card-divider w-full h-px my-3 bg-zi-border-dark/50 dark:bg-zi-border-light/50" />
            </>
          )}

          <div className="w-full flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-sm font-medium text-zi-text-light/70 dark:text-zi-text-dark/70">
                مبلغ قابل پرداخت (دلار)
              </span>
              <span className="text-lg font-normal text-zi-text-light dark:text-zi-text-dark">
                {isExpectedAmountLoading ? (
                  <span className="inline-flex min-w-10 justify-end">
                    <Spinner size={20} color="#808080" stroke={3} />
                  </span>
                ) : payablePrice ? (
                  `$${payablePrice}`
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-sm font-medium text-zi-text-light/70 dark:text-zi-text-dark/70">
                مبلغ قابل پرداخت (ریال)
              </span>
              <span className="text-lg font-normal text-zi-text-light dark:text-zi-text-dark">
                {isExpectedAmountLoading ? (
                  <span className="inline-flex min-w-10 justify-end">
                    <Spinner size={20} color="#808080" stroke={3} />
                  </span>
                ) : payablePriceIrr ? (
                  `${payablePriceIrr.toLocaleString()} ریال`
                ) : (
                  "-"
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-start items-center gap-3 pt-10">
          <button
            type="button"
            aria-label={
              ruleAgree ? "قوانین پذیرفته شده" : "قوانین پذیرفته نشده"
            }
            onClick={(e) => {
              e.stopPropagation();
              setRuleAgree((prev) => !prev);
            }}
            className={
              "inline-flex size-5 items-center justify-center rounded-md transition-all " +
              (ruleAgree
                ? "bg-zi-blue-light"
                : "bg-transparent ring-1 ring-inset ring-zi-blue-light/50")
            }
          >
            <span
              className={`text-xs ${
                ruleAgree === true ? "text-zi-text-dark" : "text-transparent"
              }`}
            >
              ✓
            </span>
            {/* {isSelected && <span className="text-white text-xs">✓</span>} */}
          </button>
          <span className="text-sm font-medium text-zi-text-light/70 dark:text-zi-text-dark/70 flex gap-1.5">
            <Link
              className="underline underline-offset-4 text-zi-blue-light"
              to="/rule/general"
            >
              قوانین کوژیتو
            </Link>
            <span>را خوانده و پذیرفته‌ام.</span>
          </span>
        </div>

        <RippleButton
          className={buttonClasses.trim()}
          onClick={handlePay}
          disabled={!canContinue}
          ruleAgree={ruleAgree}
        >
          تأیید و ادامه
        </RippleButton>
      </div>

      <div className="hidden card-divider w-full h-px my-3 bg-zi-gradient-divider md:mb-0 md:mx-5 md:w-px md:h-auto md:self-stretch md:shrink-0 md:bg-zi-gradient-divider-vertical" />
    </div>
  );
};

export const ChallengeFactor = ({ plan }) => {
  return (
    <main className="relative w-full max-w-5xl min-h-fit flex flex-col items-center justify-center mx-auto">
      <div className="w-full">
        <div className="w-full flex justify-center pt-5">
          <div className="w-full">
            <PricingCard {...plan} />
          </div>
        </div>
      </div>
    </main>
  );
};
